import { useEffect, useState, useRef } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { getInsurancePricings } from '../../../../utils/api/insurancePricing';
import { getOccupations, Occupation } from '../../../../utils/api/occupations';
import { InsurancePricing, GetInsurancePricingParams, SmokerStatus } from '../../../../interfaces/InsurancePricing';
import {
    InsurancePolicy,
    InsurancePolicyMainBranch,
    PolicyHolder,
    InsuredPersonCoverageSelection,
    SelectedCoverage,
} from '../../../../interfaces/InsurancePolicy';
import { InsuranceDiscount } from '../../../../interfaces/InsuranceDiscount';

interface FamilyMember {
    firstName: string;
    lastName: string;
    idNumber: string;
    dateOfBirth?: Date;
    gender?: string;
    familyMemberType?: string;
}

interface InsuredPersonCoverageSelectorProps {
    policy: InsurancePolicy;
    familyMembers: FamilyMember[];
    onPolicyChange: (updatedPolicy: InsurancePolicy) => void;
    insuranceDiscount?: InsuranceDiscount;
}

interface GroupedCoverage {
    coverageType: string;
    premium: number | null;
    priceIndex?: number;
    insurancePricingId: string;
}

// Check if branch requires coverage amount for price calculation
const branchRequiresCoverageAmount = (branch: InsurancePolicyMainBranch): boolean => {
    return (
        branch === InsurancePolicyMainBranch.lifeInsurance ||
        branch === InsurancePolicyMainBranch.criticalIllness ||
        branch === InsurancePolicyMainBranch.mortgageInsurance
    );
};

// Check if branch requires smoker status
const branchRequiresSmokerStatus = (branch: InsurancePolicyMainBranch): boolean => {
    return (
        branch === InsurancePolicyMainBranch.lifeInsurance ||
        branch === InsurancePolicyMainBranch.criticalIllness ||
        branch === InsurancePolicyMainBranch.mortgageInsurance
    );
};

// Calculate age from date of birth
const calculateAge = (dateOfBirth: Date | string | undefined): number | undefined => {
    if (!dateOfBirth) return undefined;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

// Get smoker status display text
const getSmokerStatusText = (status: SmokerStatus | undefined): string => {
    if (status === 'smoker') return 'מעשן';
    if (status === 'nonSmoker') return 'לא מעשן';
    return 'לא מעשן'; // default
};

// Helper function to infer spouse gender based on client gender
const inferSpouseGender = (familyMembers: FamilyMember[], spouseIdNumber: string): string | undefined => {
    // Find the client member
    const clientMember = familyMembers.find((fm) => fm.familyMemberType === 'client');

    // Find the spouse member
    const spouseMember = familyMembers.find((fm) => fm.idNumber === spouseIdNumber);

    // If spouse has a gender defined, return it
    if (spouseMember?.gender) {
        return spouseMember.gender;
    }

    // If spouse is missing gender and we have client gender, infer the opposite
    if (spouseMember?.familyMemberType === 'spouse' && clientMember?.gender) {
        return clientMember.gender === 'male' ? 'female' : 'male';
    }

    return undefined;
};

const InsuredPersonCoverageSelector = ({ policy, familyMembers, onPolicyChange, insuranceDiscount }: InsuredPersonCoverageSelectorProps) => {
    const { t } = useTranslation('insurance');

    // Extract values from policy for easier access
    const companyId = typeof policy.company === 'string' ? policy.company : policy.company?._id || '';
    const branch = policy.branch;
    const insuredPersons = policy.insuredList || [];
    const totalCoverage = policy.totalCoverage;

    // Internal state for insuredPersonCoverages to avoid circular updates
    const [insuredPersonCoverages, setInsuredPersonCoverages] = useState<InsuredPersonCoverageSelection[]>(policy.insuredPersonCoverages || []);
    const [coveragesByPerson, setCoveragesByPerson] = useState<Map<string, GroupedCoverage[]>>(new Map());
    const [loadingPersons, setLoadingPersons] = useState<Set<string>>(new Set());
    const [expandedPersons, setExpandedPersons] = useState<Set<string>>(new Set());
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const isInitialMount = useRef(true);
    const prevInsuredPersonsRef = useRef<PolicyHolder[]>([]);

    // Occupations state
    const [occupations, setOccupations] = useState<Occupation[]>([]);
    const [occupationsLoading, setOccupationsLoading] = useState(false);

    // Track if we've initialized from props to only do it once
    const initializedFromPropsRef = useRef(false);

    // Initialize internal state from props only on mount
    useEffect(() => {
        if (!initializedFromPropsRef.current && policy.insuredPersonCoverages && policy.insuredPersonCoverages.length > 0) {
            initializedFromPropsRef.current = true;
            setInsuredPersonCoverages(policy.insuredPersonCoverages);
        }
    }, []);

    // Fetch occupations when company changes
    useEffect(() => {
        const fetchOccupations = async () => {
            console.log('Fetching occupations for companyId:', companyId);
            if (!companyId) {
                setOccupations([]);
                return;
            }

            setOccupationsLoading(true);
            try {
                const response = await getOccupations({ company: companyId, limit: 500 });
                console.log('Occupations response:', response);
                setOccupations(response.occupations || []);
            } catch (error) {
                console.error('Failed to fetch occupations:', error);
                setOccupations([]);
            } finally {
                setOccupationsLoading(false);
            }
        };

        fetchOccupations();
    }, [companyId]);

    // Create stable key for insuredPersons to avoid triggering effect on array reference changes
    const insuredPersonsKey = insuredPersons
        .map((p) => p.idNumber)
        .sort()
        .join(',');

    // Helper function to update policy with new persons and coverages
    const updatePolicy = (newPersons: PolicyHolder[], newCoverages: InsuredPersonCoverageSelection[]) => {
        setInsuredPersonCoverages(newCoverages);
        onPolicyChange({
            ...policy,
            insuredList: newPersons,
            insuredPersonCoverages: newCoverages,
        });
    };

    // Check if a person is selected as insured
    const isPersonSelected = (idNumber: string): boolean => {
        return insuredPersons.some((p) => p.idNumber === idNumber);
    };

    // Handle person selection toggle
    const handlePersonToggle = async (member: FamilyMember) => {
        const isSelected = isPersonSelected(member.idNumber);

        if (isSelected) {
            // Remove person from insured list
            const newInsuredPersons = insuredPersons.filter((p) => p.idNumber !== member.idNumber);

            // Also remove their coverages
            const newCoverages = insuredPersonCoverages.filter((ipc) => ipc.insuredPerson.idNumber !== member.idNumber);

            // Update policy
            updatePolicy(newInsuredPersons, newCoverages);

            // Collapse the person
            setExpandedPersons((prev) => {
                const newSet = new Set(prev);
                newSet.delete(member.idNumber);
                return newSet;
            });
        } else {
            // Add person to insured list
            const smokerStatus: SmokerStatus = 'nonSmoker';
            const newPolicyHolder: PolicyHolder = {
                name: `${member.firstName} ${member.lastName}`,
                idNumber: member.idNumber,
                dateOfBirth: member.dateOfBirth,
                gender: member.gender as any,
                smokerStatus: smokerStatus,
                occupation: '',
                occupationMultiplier: 1,
            };

            const newInsuredPersons = [...insuredPersons, newPolicyHolder];

            // Update policy (coverages unchanged when adding a person)
            updatePolicy(newInsuredPersons, insuredPersonCoverages);

            // Expand the person to show coverages
            setExpandedPersons((prev) => new Set(prev).add(member.idNumber));

            // Fetch coverages for this person - pass current state to avoid stale closure
            const result = await fetchCoveragesForPerson(newPolicyHolder, smokerStatus, newInsuredPersons, insuredPersonCoverages);
            setCoveragesByPerson((prev) => {
                const newMap = new Map(prev);
                newMap.set(member.idNumber, result.sortedCoverages);
                return newMap;
            });

            // If there's an auto-selected coverage, update the policy
            if (result.autoSelectedCoverage) {
                const newCoverages: InsuredPersonCoverageSelection[] = [
                    ...insuredPersonCoverages,
                    {
                        insuredPerson: {
                            name: newPolicyHolder.name,
                            idNumber: newPolicyHolder.idNumber,
                            dateOfBirth: newPolicyHolder.dateOfBirth,
                            gender: newPolicyHolder.gender,
                        },
                        selectedCoverages: [result.autoSelectedCoverage],
                        smokerStatus,
                    },
                ];
                updatePolicy(newInsuredPersons, newCoverages);
            }
        }
    };

    // Get smoker status for a person
    const getSmokerStatusForPerson = (personIdNumber: string): SmokerStatus | undefined => {
        const entry = insuredPersons.find((ipc) => ipc.idNumber === personIdNumber);
        return entry?.smokerStatus as SmokerStatus | undefined;
    };

    // Handle smoker status change
    const handleSmokerStatusChange = (personIdNumber: string, smokerStatus: SmokerStatus) => {
        const person = insuredPersons.find((p) => p.idNumber === personIdNumber);
        if (!person) return;

        const existingEntry = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === personIdNumber);

        let newCoverages: InsuredPersonCoverageSelection[];

        if (existingEntry) {
            newCoverages = insuredPersonCoverages.map((ipc) => {
                if (ipc.insuredPerson.idNumber === personIdNumber) {
                    return {
                        ...ipc,
                        smokerStatus,
                    };
                }
                return ipc;
            });
        } else {
            newCoverages = [
                ...insuredPersonCoverages,
                {
                    insuredPerson: { name: person.name, idNumber: person.idNumber, dateOfBirth: person.dateOfBirth, gender: person.gender },
                    selectedCoverages: [],
                    smokerStatus,
                },
            ];
        }

        // Update insuredPersons with the new smokerStatus
        const newInsuredPersons = insuredPersons.map((p) => {
            if (p.idNumber === personIdNumber) {
                return {
                    ...p,
                    smokerStatus,
                };
            }
            return p;
        });

        // Update policy
        updatePolicy(newInsuredPersons, newCoverages);

        // Clear cached coverages for this person to force re-fetch with new smoker status
        setCoveragesByPerson((prev) => {
            const newMap = new Map(prev);
            newMap.delete(personIdNumber);
            return newMap;
        });

        // Trigger re-fetch with new smoker status
        fetchCoveragesForPerson(person, smokerStatus).then(({ sortedCoverages }) => {
            setCoveragesByPerson((prev) => {
                const newMap = new Map(prev);
                newMap.set(personIdNumber, sortedCoverages);
                return newMap;
            });
        });
    };

    // Toggle smoker status between smoker and nonSmoker
    const toggleSmokerStatus = (e: React.MouseEvent, personIdNumber: string) => {
        e.stopPropagation(); // Prevent accordion from toggling
        const currentStatus = getSmokerStatusForPerson(personIdNumber) || 'nonSmoker';
        const newStatus: SmokerStatus = currentStatus === 'smoker' ? 'nonSmoker' : 'smoker';
        handleSmokerStatusChange(personIdNumber, newStatus);
    };

    // Handle occupation selection from dropdown
    const handleOccupationSelect = (personIdNumber: string, selectedOccupation: Occupation | null) => {
        const newInsuredPersons = insuredPersons.map((p) => {
            if (p.idNumber === personIdNumber) {
                return {
                    ...p,
                    occupation: selectedOccupation?.name || '',
                    occupationMultiplier: selectedOccupation?.multiplier || 1,
                };
            }
            return p;
        });
        updatePolicy(newInsuredPersons, insuredPersonCoverages);
    };

    // Fetch coverages for a specific person
    // currentInsuredPersons and currentCoverages are passed to handle stale closure issues
    // Returns both the coverages and an optional auto-selected coverage
    const fetchCoveragesForPerson = async (
        person: PolicyHolder,
        smokerStatus?: SmokerStatus,
        currentInsuredPersons?: PolicyHolder[],
        currentCoverages?: InsuredPersonCoverageSelection[],
    ): Promise<{ sortedCoverages: GroupedCoverage[]; autoSelectedCoverage?: SelectedCoverage }> => {
        // Use passed values or fall back to current state (for cases like useEffect)
        const coveragesToUse = currentCoverages ?? insuredPersonCoverages;
        if (!companyId || !branch || !person.idNumber) {
            return { sortedCoverages: [] };
        }

        setLoadingPersons((prev) => new Set(prev).add(person.idNumber));
        let sortedCoverages: GroupedCoverage[] = [];
        let autoSelectedCoverage: SelectedCoverage | undefined;

        try {
            const personAge = calculateAge(person.dateOfBirth);

            const params: GetInsurancePricingParams = {
                companyId,
                branch,
                limit: 500,
            };

            // Add person-specific params if available
            if (personAge !== undefined) {
                params.personAge = personAge;
            }

            // Infer gender for spouse if missing
            const genderToUse = person.gender || inferSpouseGender(familyMembers, person.idNumber);
            if (genderToUse) {
                params.personGender = genderToUse as any;
            }

            if (smokerStatus) {
                params.personSmoker = smokerStatus;
            }

            const response = await getInsurancePricings(params);
            const pricings: InsurancePricing[] = response.pricings || [];

            // Group by coverage type
            const coverageMap = new Map<string, GroupedCoverage>();

            pricings.forEach((pricing) => {
                if (!coverageMap.has(pricing.coverageType)) {
                    coverageMap.set(pricing.coverageType, {
                        coverageType: pricing.coverageType,
                        premium: pricing.premium,
                        priceIndex: pricing.priceIndex,
                        insurancePricingId: pricing._id,
                    });
                }
            });

            sortedCoverages = Array.from(coverageMap.values()).sort((a, b) => a.coverageType.localeCompare(b.coverageType));

            // Auto-select if only one coverage is returned
            if (sortedCoverages.length === 1) {
                const singleCoverage = sortedCoverages[0];

                // Check if this person already has coverages selected - if so, don't auto-select
                const existingEntry = coveragesToUse.find((ipc) => ipc.insuredPerson.idNumber === person.idNumber);
                const alreadyHasCoverages = existingEntry && existingEntry.selectedCoverages.length > 0;

                if (!alreadyHasCoverages) {
                    autoSelectedCoverage = {
                        coverageType: singleCoverage.coverageType,
                        insurancePricingId: singleCoverage.insurancePricingId,
                    };
                }
            }
        } catch (error) {
            console.error('Failed to fetch coverages for person', error);
        } finally {
            setLoadingPersons((prev) => {
                const newSet = new Set(prev);
                newSet.delete(person.idNumber);
                return newSet;
            });
        }
        return { sortedCoverages, autoSelectedCoverage };
    };

    // Helper to enrich person with family member data (dateOfBirth, gender)
    const enrichPersonWithFamilyData = (person: PolicyHolder): PolicyHolder => {
        // If person already has dateOfBirth, return as is
        if (person.dateOfBirth) return person;

        // Find matching family member
        const familyMember = familyMembers.find((fm) => fm.idNumber === person.idNumber);
        if (familyMember) {
            return {
                ...person,
                dateOfBirth: familyMember.dateOfBirth,
                gender: familyMember.gender as any,
            };
        }
        return person;
    };

    // Reset coverages and re-fetch when company or branch changes
    const handleExtendedPersons = async (isInitialLoad: boolean = false) => {
        // Set loading state
        if (!isInitialLoad) {
            setInitialLoadComplete(false);
        }

        const newExpanded = new Set<string>();

        // Fetch coverages for all persons sequentially
        const map = new Map<string, GroupedCoverage[]>();
        let updatedCoverages = insuredPersonCoverages;
        // Track enriched persons to update policy with dateOfBirth/gender from familyMembers
        const enrichedPersonsMap = new Map<string, PolicyHolder>();

        for (const person of insuredPersons) {
            newExpanded.add(person.idNumber);
            const smokerStatus = getSmokerStatusForPerson(person.idNumber) || 'nonSmoker';
            const enrichedPerson = enrichPersonWithFamilyData(person);
            enrichedPersonsMap.set(person.idNumber, enrichedPerson);
            const { sortedCoverages, autoSelectedCoverage } = await fetchCoveragesForPerson(
                enrichedPerson,
                smokerStatus,
                insuredPersons,
                updatedCoverages
            );
            map.set(person.idNumber, sortedCoverages);

            // If there's an auto-selected coverage, add it to the accumulated coverages
            if (autoSelectedCoverage) {
                const existingEntry = updatedCoverages.find((ipc) => ipc.insuredPerson.idNumber === person.idNumber);

                if (existingEntry) {
                    updatedCoverages = updatedCoverages.map((ipc) => {
                        if (ipc.insuredPerson.idNumber === person.idNumber) {
                            return {
                                ...ipc,
                                selectedCoverages: [autoSelectedCoverage],
                            };
                        }
                        return ipc;
                    });
                } else {
                    updatedCoverages = [
                        ...updatedCoverages,
                        {
                            insuredPerson: {
                                name: enrichedPerson.name,
                                idNumber: enrichedPerson.idNumber,
                                dateOfBirth: enrichedPerson.dateOfBirth,
                                gender: enrichedPerson.gender,
                            },
                            selectedCoverages: [autoSelectedCoverage],
                            smokerStatus,
                        },
                    ];
                }
            }
        }

        setCoveragesByPerson(map);

        // Build enriched insuredPersons list with dateOfBirth/gender from familyMembers
        const enrichedInsuredPersons = insuredPersons.map((person) => {
            const enriched = enrichedPersonsMap.get(person.idNumber);
            return enriched || person;
        });

        // Update policy with enriched persons (includes dateOfBirth/gender)
        updatePolicy(enrichedInsuredPersons, updatedCoverages);

        setExpandedPersons(newExpanded);
        // Mark initial load as complete after all persons are loaded
        setInitialLoadComplete(true);
    };

    // Effect to handle initial load and company/branch changes
    useEffect(() => {
        // Check if insured persons have changed (by comparing ID lists)
        const currentIds = insuredPersons
            .map((p) => p.idNumber)
            .sort()
            .join(',');
        const prevIds = prevInsuredPersonsRef.current
            .map((p) => p.idNumber)
            .sort()
            .join(',');
        const insuredPersonsChanged = currentIds !== prevIds;

        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevInsuredPersonsRef.current = insuredPersons;
            if (insuredPersons.length > 0) {
                // On initial mount, load coverages for existing insured persons
                handleExtendedPersons(true);
            } else {
                // No insured persons, mark as loaded
                setInitialLoadComplete(true);
            }
        } else if (insuredPersonsChanged && insuredPersons.length > 0 && prevInsuredPersonsRef.current.length === 0) {
            // Insured persons were just populated (e.g., from appendices rebuild)
            prevInsuredPersonsRef.current = insuredPersons;
            handleExtendedPersons(true);
        } else if (insuredPersonsChanged) {
            // Insured persons changed, reload everything
            prevInsuredPersonsRef.current = insuredPersons;
            handleExtendedPersons(false);
        }
        // Note: We don't include insuredPersonCoverages in dependencies to avoid infinite loops
        // when the parent updates the policy object after we call onInsuredPersonsAndCoveragesChange
        // Note: Using insuredPersonsKey instead of insuredPersons to only trigger when IDs actually change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branch, insuredPersonsKey]);

    const handleCoverageToggle = (personIdNumber: string, coverage: GroupedCoverage) => {
        const person = insuredPersons.find((p) => p.idNumber === personIdNumber);
        if (!person) return;

        const existingEntry = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === personIdNumber);
        const selectedCoverage: SelectedCoverage = {
            coverageType: coverage.coverageType,
            insurancePricingId: coverage.insurancePricingId,
        };

        let newCoverages: InsuredPersonCoverageSelection[];

        if (existingEntry) {
            // Check if already selected (by insurancePricingId or coverageType)
            const isSelected = isCoverageSelected(personIdNumber, coverage);
            newCoverages = insuredPersonCoverages.map((ipc) => {
                if (ipc.insuredPerson.idNumber === personIdNumber) {
                    return {
                        ...ipc,
                        selectedCoverages: isSelected
                            ? ipc.selectedCoverages.filter(
                                  (sc) => sc.insurancePricingId !== coverage.insurancePricingId && sc.coverageType !== coverage.coverageType,
                              )
                            : [...ipc.selectedCoverages, selectedCoverage],
                    };
                }
                return ipc;
            });
        } else {
            // Create new entry
            newCoverages = [
                ...insuredPersonCoverages,
                {
                    insuredPerson: { name: person.name, idNumber: person.idNumber, dateOfBirth: person.dateOfBirth, gender: person.gender },
                    selectedCoverages: [selectedCoverage],
                },
            ];
        }

        // Update policy (persons unchanged)
        updatePolicy(insuredPersons, newCoverages);
    };

    // Handle select all / deselect all coverages for a person
    const handleSelectAllCoverages = (personIdNumber: string, personCoverages: GroupedCoverage[]) => {
        const person = insuredPersons.find((p) => p.idNumber === personIdNumber);
        if (!person) return;

        const selectedCoverageTypes = getSelectedCoveragesForPerson(personIdNumber);
        const allCoverageTypes = personCoverages.map((c) => c.coverageType);
        const allSelected = allCoverageTypes.every((ct) => selectedCoverageTypes.includes(ct));

        // Convert GroupedCoverage[] to SelectedCoverage[]
        const allSelectedCoverages: SelectedCoverage[] = personCoverages.map((c) => ({
            coverageType: c.coverageType,
            insurancePricingId: c.insurancePricingId,
        }));

        const existingEntry = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === personIdNumber);

        let newCoverages: InsuredPersonCoverageSelection[];

        if (existingEntry) {
            newCoverages = insuredPersonCoverages.map((ipc) => {
                if (ipc.insuredPerson.idNumber === personIdNumber) {
                    return {
                        ...ipc,
                        selectedCoverages: allSelected ? [] : allSelectedCoverages,
                    };
                }
                return ipc;
            });
        } else {
            newCoverages = [
                ...insuredPersonCoverages,
                {
                    insuredPerson: { name: person.name, idNumber: person.idNumber, dateOfBirth: person.dateOfBirth, gender: person.gender },
                    selectedCoverages: allSelectedCoverages,
                },
            ];
        }

        // Update policy
        updatePolicy(insuredPersons, newCoverages);
    };

    // Toggle expansion for a person's coverages
    const togglePersonExpansion = (personIdNumber: string) => {
        setExpandedPersons((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(personIdNumber)) {
                newSet.delete(personIdNumber);
            } else {
                newSet.add(personIdNumber);
            }
            return newSet;
        });
    };

    const getSelectedCoveragesForPerson = (personIdNumber: string): string[] => {
        const entry = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === personIdNumber);
        return entry?.selectedCoverages.map((sc) => sc.coverageType) || [];
    };

    // Check if a coverage is selected - matches by insurancePricingId first, falls back to coverageType
    // Get discount percentage for a specific coverage type
    const getDiscountPercentageForCoverage = (coverageType: string): number => {
        if (!insuranceDiscount || !insuranceDiscount.discountYears || insuranceDiscount.discountYears.length === 0) {
            return 0;
        }

        // Get year 1 discount
        const firstYearDiscount = insuranceDiscount.discountYears.find((dy) => dy.year === 1);
        if (!firstYearDiscount) return 0;

        // Check if there's an exception for this coverage type
        if (firstYearDiscount.exceptions && firstYearDiscount.exceptions.length > 0) {
            const exception = firstYearDiscount.exceptions.find((ex) => ex.coverageType === coverageType);
            if (exception) {
                return exception.percentage;
            }
        }

        // Return default percentage
        return firstYearDiscount.percentage;
    };

    const isCoverageSelected = (personIdNumber: string, coverage: GroupedCoverage): boolean => {
        const entry = insuredPersonCoverages.find((ipc) => ipc.insuredPerson.idNumber === personIdNumber);
        if (!entry) return false;

        // First try to match by insurancePricingId (for editing existing policies)
        const matchById = entry.selectedCoverages.some((sc) => sc.insurancePricingId === coverage.insurancePricingId);
        if (matchById) return true;

        // Fall back to matching by coverageType (for newly selected coverages)
        return entry.selectedCoverages.some((sc) => sc.coverageType === coverage.coverageType);
    };

    // Count selected coverages for a person (using isCoverageSelected for accurate count)
    const getSelectedCountForPerson = (personIdNumber: string): number => {
        const personCoverages = coveragesByPerson.get(personIdNumber) || [];
        return personCoverages.filter((c) => isCoverageSelected(personIdNumber, c)).length;
    };

    const formatPrice = (premium: number | null, coverageType: string, personIdNumber: string): string => {
        if (premium === null) return t('Price not available');

        let displayPrice = premium;

        // For life insurance and critical illness, multiply by coverage amount
        if (branchRequiresCoverageAmount(branch) && totalCoverage) {
            displayPrice = premium * (totalCoverage / 1000);
        }

        // Apply discount if available (check for coverage-specific exception)
        const discountPercentage = getDiscountPercentageForCoverage(coverageType);
        if (discountPercentage > 0) {
            displayPrice = displayPrice * (1 - discountPercentage / 100);
        }

        // Apply occupation multiplier
        const person = insuredPersons.find((p) => p.idNumber === personIdNumber);
        const occupationMultiplier = person?.occupationMultiplier ?? 1;
        displayPrice = displayPrice * occupationMultiplier;

        return `${displayPrice.toFixed(2)} ₪`;
    };

    const getTotalSelectedCount = (): number => {
        let total = 0;
        for (const person of insuredPersons) {
            total += getSelectedCountForPerson(person.idNumber);
        }
        return total;
    };

    // Calculate the display price for a given premium
    const calculateDisplayPrice = (premium: number | null, coverageType: string, personIdNumber: string): number => {
        if (premium === null) return 0;

        let displayPrice = premium;

        // For life insurance and critical illness, multiply by coverage amount
        if (branchRequiresCoverageAmount(branch) && totalCoverage) {
            displayPrice = premium * (totalCoverage / 1000);
        }

        // Apply discount if available (check for coverage-specific exception)
        const discountPercentage = getDiscountPercentageForCoverage(coverageType);
        if (discountPercentage > 0) {
            displayPrice = displayPrice * (1 - discountPercentage / 100);
        }

        // Apply occupation multiplier
        const person = insuredPersons.find((p) => p.idNumber === personIdNumber);
        const occupationMultiplier = person?.occupationMultiplier ?? 1;
        displayPrice = displayPrice * occupationMultiplier;

        return displayPrice;
    };

    // Get total price for a person's selected coverages
    const getTotalPriceForPerson = (personIdNumber: string): number => {
        const personCoverages = coveragesByPerson.get(personIdNumber) || [];

        let total = 0;
        for (const coverage of personCoverages) {
            if (isCoverageSelected(personIdNumber, coverage) && coverage.premium !== null) {
                total += calculateDisplayPrice(coverage.premium, coverage.coverageType, personIdNumber);
            }
        }
        return total;
    };

    // Get grand total price for all selected coverages
    const getGrandTotalPrice = (): number => {
        let total = 0;
        for (const ipc of insuredPersonCoverages) {
            total += getTotalPriceForPerson(ipc.insuredPerson.idNumber);
        }
        return total;
    };

    if (familyMembers.length === 0) {
        return (
            <div className="text-muted py-2" style={{ textAlign: 'right', direction: 'rtl' }}>
                {t('No family members available')}
            </div>
        );
    }

    const requiresSmoker = branchRequiresSmokerStatus(branch);

    return (
        <div className="insured-person-coverage-selector" dir="rtl" style={{ direction: 'rtl' }}>
            <div className="family-members-list border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                {familyMembers.map((member) => {
                    const isSelected = isPersonSelected(member.idNumber);
                    const isExpanded = expandedPersons.has(member.idNumber);
                    const personCoverages = coveragesByPerson.get(member.idNumber) || [];
                    const isLoading = loadingPersons.has(member.idNumber);
                    const personAge = calculateAge(member.dateOfBirth);
                    const smokerStatus = getSmokerStatusForPerson(member.idNumber);

                    const person = insuredPersons.find((p) => p.idNumber === member.idNumber);

                    // Debug: log occupation matching
                    if (person?.occupation) {
                        console.log('Person occupation:', person.occupation, 'Found in list:', occupations.find((o) => o.name === person.occupation));
                    }

                    return (
                        <div key={member.idNumber} className="person-row border-bottom">
                            {/* Person header row with checkbox */}
                            <div className="d-flex align-items-center justify-content-between p-2 bg-white" style={{ direction: 'rtl' }}>
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <input
                                        type="checkbox"
                                        id={`person-${member.idNumber}`}
                                        checked={isSelected}
                                        onChange={() => handlePersonToggle(member)}
                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                    />
                                    <label htmlFor={`person-${member.idNumber}`} className="fw-medium mb-0" style={{ cursor: 'pointer' }}>
                                        {member.firstName} {member.lastName}
                                    </label>
                                    {personAge !== undefined && (
                                        <span className="text-muted" style={{ fontSize: '0.85em' }}>
                                            ({t('Age')}: {personAge})
                                        </span>
                                    )}
                                    {isSelected && requiresSmoker && (
                                        <span
                                            className="d-flex align-items-center gap-1"
                                            style={{ fontSize: '0.85em', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSmokerStatus(e, member.idNumber);
                                            }}
                                            title={t('Click to change smoker status')}
                                        >
                                            {smokerStatus === 'smoker' ? (
                                                <i className="mdi mdi-smoking text-danger" style={{ fontSize: '1.1em' }}></i>
                                            ) : (
                                                <i className="mdi mdi-smoking-off text-success" style={{ fontSize: '1.1em' }}></i>
                                            )}
                                            <span className={smokerStatus === 'smoker' ? 'text-danger' : 'text-success'}>
                                                {getSmokerStatusText(smokerStatus)}
                                            </span>
                                        </span>
                                    )}
                                    {isSelected && (
                                        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.85em' }} onClick={(e) => e.stopPropagation()}>
                                            <Select<Occupation>
                                                value={
                                                    person?.occupation
                                                        ? occupations.find((o) => o.name === person.occupation) || null
                                                        : null
                                                }
                                                onChange={(selected) => handleOccupationSelect(member.idNumber, selected as Occupation | null)}
                                                options={occupations}
                                                getOptionLabel={(option: Occupation) => option.multiplier === 1 ? option.name : `${option.name} (x${option.multiplier})`}
                                                getOptionValue={(option: Occupation) => option._id}
                                                placeholder={t('Select occupation')}
                                                isClearable
                                                isLoading={occupationsLoading}
                                                noOptionsMessage={() => t('No occupations found')}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '28px',
                                                        height: '28px',
                                                        fontSize: '0.85em',
                                                        minWidth: '180px',
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        height: '28px',
                                                        padding: '0 6px',
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0',
                                                        padding: '0',
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '28px',
                                                    }),
                                                    dropdownIndicator: (base) => ({
                                                        ...base,
                                                        padding: '4px',
                                                    }),
                                                    clearIndicator: (base) => ({
                                                        ...base,
                                                        padding: '4px',
                                                    }),
                                                    option: (base) => ({
                                                        ...base,
                                                        fontSize: '0.85em',
                                                        padding: '6px 10px',
                                                        direction: 'rtl',
                                                        textAlign: 'right',
                                                    }),
                                                    menuList: (base) => ({
                                                        ...base,
                                                        direction: 'rtl',
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                    menuPortal: (base) => ({
                                                        ...base,
                                                        zIndex: 9999,
                                                    }),
                                                }}
                                                menuPortalTarget={document.body}
                                            />
                                            {person?.occupationMultiplier && person.occupationMultiplier !== 1 && (
                                                <span className="text-muted" style={{ fontSize: '0.9em', marginLeft: '4px' }}>
                                                    x{person.occupationMultiplier}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {isSelected && getSelectedCountForPerson(member.idNumber) > 0 && (
                                        <span className="badge bg-success">
                                            {getSelectedCountForPerson(member.idNumber)} {t('coverages')} -{' '}
                                            {getTotalPriceForPerson(member.idNumber).toFixed(2)} ₪
                                        </span>
                                    )}
                                    {isSelected && getSelectedCountForPerson(member.idNumber) === 0 && (
                                        <span className="badge bg-warning text-dark">{t('No coverages selected')}</span>
                                    )}
                                    {isSelected && (
                                        <i
                                            className={`mdi ${isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'} text-muted`}
                                            style={{ fontSize: '1.3em', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                togglePersonExpansion(member.idNumber);
                                            }}
                                            title={isExpanded ? t('Collapse') : t('Expand')}
                                        ></i>
                                    )}
                                </div>
                            </div>

                            {/* Coverages dropdown - shown when person is selected and expanded */}
                            {isSelected && isExpanded && (
                                <div className="coverages-dropdown pe-4" style={{ backgroundColor: '#fafafa' }}>
                                    {isLoading || !initialLoadComplete ? (
                                        <div className="d-flex align-items-center justify-content-end gap-2 py-2 pe-2">
                                            <Spinner animation="border" size="sm" variant="success" />
                                            <span className="small">{t('Loading coverages...')}</span>
                                        </div>
                                    ) : personCoverages.length === 0 ? (
                                        <div className="text-muted py-2 pe-2 small" style={{ textAlign: 'right' }}>
                                            {t('No coverages available for selected company and branch')}
                                        </div>
                                    ) : (
                                        <>
                                            {/* Select All option - shown if more than 2 coverages */}
                                            {personCoverages.length > 2 && (
                                                <div
                                                    className="select-all-row d-flex align-items-center justify-content-between py-1 px-2 border-bottom"
                                                    style={{ direction: 'rtl', backgroundColor: '#f0f0f0' }}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`select-all-${member.idNumber}`}
                                                        reverse
                                                        className="form-check-success"
                                                    >
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={personCoverages.every((c) => isCoverageSelected(member.idNumber, c))}
                                                            onChange={() => handleSelectAllCoverages(member.idNumber, personCoverages)}
                                                        />
                                                        <Form.Check.Label
                                                            style={{ marginRight: '8px', cursor: 'pointer', fontSize: '0.9em', fontWeight: 500 }}
                                                        >
                                                            {personCoverages.every((c) => isCoverageSelected(member.idNumber, c))
                                                                ? t('Deselect All')
                                                                : t('Select All')}
                                                        </Form.Check.Label>
                                                    </Form.Check>
                                                </div>
                                            )}
                                            <div
                                                className="coverage-list"
                                                style={{
                                                    maxHeight: '180px',
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                {personCoverages.map((coverage) => (
                                                    <div
                                                        key={coverage.coverageType}
                                                        className="coverage-item d-flex align-items-center justify-content-between py-1 px-2"
                                                        style={{ direction: 'rtl' }}
                                                    >
                                                        <Form.Check
                                                            type="checkbox"
                                                            id={`coverage-${member.idNumber}-${coverage.coverageType}`}
                                                            reverse
                                                            className="form-check-success"
                                                        >
                                                            <Form.Check.Input
                                                                type="checkbox"
                                                                checked={isCoverageSelected(member.idNumber, coverage)}
                                                                onChange={() => handleCoverageToggle(member.idNumber, coverage)}
                                                            />
                                                            <Form.Check.Label style={{ marginRight: '8px', cursor: 'pointer', fontSize: '0.9em' }}>
                                                                {coverage.coverageType}
                                                            </Form.Check.Label>
                                                        </Form.Check>
                                                        <span className="text-success fw-medium small" style={{ whiteSpace: 'nowrap' }}>
                                                            {formatPrice(coverage.premium, coverage.coverageType, member.idNumber)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {getTotalSelectedCount() > 0 && (
                <div className="mt-2 text-success small" style={{ textAlign: 'right' }}>
                    {t('Total selected')}: {getTotalSelectedCount()} {t('coverages')} - {getGrandTotalPrice().toFixed(2)} ₪
                </div>
            )}
        </div>
    );
};

export default InsuredPersonCoverageSelector;
